import { CheckListSquareIcon, EditIcon } from '../../../../../assets/icons';
import { selectSelectedJob } from '../../../../../store/workspace/selectors';
import { getJobDetailThunk } from '../../../../../store/workspace/thunks';
import { thousandSeparator } from '../../../../../utils/numberHelpers';
import { useDispatch, useSelector } from '../../../../../store/store';
import { PostedJob } from '../../../../../store/workspace/types';
import Spinner from '../../../../../components/Spinner';
import Button from '../../../../../components/Button';
import Chips from '../../../../../components/Chips';
import Modal from '../../../../../components/Modal';
import { useNavigate } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';
import styles from './style.module.scss';
import {
    setConfirmEndExam,
    setJobDetailModal,
    setPreparingExamsModal,
} from '../../../../../store/workspace/slice';

const EMPTY_LIST = '----';

interface SectionProps {
    title: string;
    children: ReactNode;
};

function Section({ title, children }: SectionProps) {
    return (
        <div className={styles.section}>
            <div className={styles.titleBar}>
                <span>{title}</span>
            </div>
            <div className={styles.info_summary}>
                {children}
            </div>
        </div>
    );
};

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className={styles.info_summary_item}>
            <span>{label}</span>
            {children}
        </div>
    );
};

function ChipList({ items }: { items?: string[] }) {
    return (
        <div className={styles.arrayList}>
            {items?.length === 0 ? EMPTY_LIST : items?.map(item => <Chips key={item} title={item} />)}
        </div>
    );
};

const yesNo = (value?: boolean) => value ? 'Yes' : 'No';

export default function JobDetail() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectedJob = useSelector(selectSelectedJob);

    const [job, setJob] = useState<PostedJob | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const closeModal = () => dispatch(setJobDetailModal(false));

    useEffect(() => {
        setIsFetching(true);
        dispatch(getJobDetailThunk({ job: selectedJob?.id }))
            .unwrap()
            .then(setJob)
            .catch(() => undefined)
            .finally(() => setIsFetching(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Re-reads the job first: its exams may still have been generating when the
     * modal was opened, and an incomplete job cannot be edited.
     */
    const editJobPost = () => {
        if (!job?.id) return;

        setIsEditing(true);
        dispatch(getJobDetailThunk({ job: job.id }))
            .unwrap()
            .then((freshJob) => {
                setJob(freshJob);

                const examsPending = (freshJob.multipleChoiceExam && !freshJob.multipleChoiceExamId) ||
                    (freshJob.codingExam && !freshJob.codingExamId);

                if (examsPending) {
                    dispatch(setPreparingExamsModal(true));
                    return;
                }

                navigate(`/workspace/edit-job?id=${freshJob.id}`);
                closeModal();
            })
            .finally(() => {
                setIsEditing(false);
            });
    };

    return (
        <Modal
            size='xxl'
            restrictActionsWidth
            title={job?.title || ''}
            icon={<CheckListSquareIcon />}
            childClassName={styles.wrapper}
            closeFunc={closeModal}
            actions={!isFetching && job ? (job.status === 'posted' ? 'both' : 'dismiss') : 'none'}
            dismissButton={{
                text: 'Close',
                type: 'Outline-Dark',
                onClick: closeModal,
            }}
            confirmButton={{
                text: 'End job post',
                type: 'Error',
                onClick: () => dispatch(setConfirmEndExam(true)),
            }}
        >
            {isFetching && <Spinner />}

            {!isFetching && job && <>
                {job.status === 'unposted' && <div className={styles.actions}>
                    <Button
                        type='Outline-Dark'
                        text='Edit job post'
                        icon={<EditIcon />}
                        iconPosition='Left'
                        className={styles.actions_btn}
                        onClick={editJobPost}
                        loading={isEditing}
                    />
                </div>}

                <Section title='Job information'>
                    <Row label='Job Title'>
                        <span>{job.title}</span>
                    </Row>
                    <Row label='Job Base Salary Amount'>
                        <span>
                            {thousandSeparator(job.baseSalaryAmount || '-')}$ {job.baseSalaryFrequency?.title}
                        </span>
                    </Row>
                    <Row label='Workplace Type'>
                        <span>{job.workplaceType?.title}</span>
                    </Row>
                    <Row label='Job type'>
                        <span>{job.jobType?.title}</span>
                    </Row>
                    <Row label='City (Job Location)'>
                        <span>{job.city?.city}, {job.state?.state} {job.country?.title}</span>
                    </Row>
                    <div className={styles.info_summary_description}>
                        <span>Job Description</span>
                        <p>{job.description}</p>
                    </div>
                </Section>

                <Section title='Recruiting interview'>
                    <Row label='Generate recruiting interview'>
                        <span>{yesNo(job.recruitingInterview)}</span>
                    </Row>
                    <Row label='Recruiting interview questions'>
                        <div className={styles.questionList}>
                            {job.recruitingInterviewQuestions?.map((question, index) => <span key={index}>
                                <b>{index + 1}-</b> {question}
                            </span>)}
                        </div>
                    </Row>
                </Section>

                <Section title='Multiple choice exam'>
                    <Row label='Generate multiple choice exam'>
                        <span>{yesNo(job.multipleChoiceExam)}</span>
                    </Row>
                    <Row label='Have coding questions'>
                        <span>{yesNo(job.multipleChoiceCodingExam)}</span>
                    </Row>
                    <Row label='Languages for coding questions'>
                        <ChipList items={job.multipleChoiceCodingExamLanguages} />
                    </Row>
                    <Row label='Skills to cover in greater details:'>
                        <div className={styles.arrayList}>
                            {job.multipleChoiceExamSkills?.map(skill => <Chips key={skill} title={skill} />)}
                        </div>
                    </Row>
                </Section>

                <Section title='Coding exam'>
                    <Row label='Generate coding exam'>
                        <span>{yesNo(job.codingExam)}</span>
                    </Row>
                    <Row label='Programming languages'>
                        <ChipList items={job.codingExamLanguages} />
                    </Row>
                </Section>
            </>}
        </Modal>
    );
};
